// Libararies
import React from 'react';

// Supermove
import {Popover} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer, useModal, usePopover, useResponsive, useSheet} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import ActionMenu from '@shared/design/components/ActionMenu';
import GenerateProjectDocumentsForJobModal from '@shared/modules/Project/components/Document/GenerateProjectDocumentsForJobModal';
import ProjectDocumentTemplateViewer from '@shared/modules/Project/components/Document/ProjectDocumentTemplateViewer';

type Props = {
  project: ProjectModel;
  refetch?: () => void;
  children: (props: {handleToggle: () => void}) => React.ReactNode;
};

const AddProjectDocumentsPopover = ({project, refetch = () => {}, children}: Props) => {
  const responsive = useResponsive();
  const generateCustomDocumentsDrawer = useDrawer({name: 'Generate Custom Documents Drawer'});
  const generateCustomDocumentsSheet = useSheet({name: 'Generate Custom Documents Sheet'});
  const generateCrewDocumentsModal = useModal({name: 'Generate Crew Documents Modal'});
  const overlayHandler = responsive.desktop
    ? generateCustomDocumentsDrawer
    : generateCustomDocumentsSheet;
  const addDocumentsPopover = usePopover({name: 'Add Documents Popover'});
  return (
    <React.Fragment>
      <Popover.Content innerRef={addDocumentsPopover.ref}>
        {children({handleToggle: addDocumentsPopover.handleToggle})}
      </Popover.Content>
      <Popover
        placement={Popover.Positions.BottomEnd}
        isOpen={addDocumentsPopover.isOpen}
        handleOpen={addDocumentsPopover.handleOpen}
        handleClose={addDocumentsPopover.handleClose}
        reference={addDocumentsPopover.ref}
        offset={[0, 4]}
      >
        <ActionMenu
          key={addDocumentsPopover.key}
          handleClose={addDocumentsPopover.handleClose}
          actions={[
            {
              text: 'Add new document',
              onPress: overlayHandler.handleOpen,
            },
            {
              text: 'Generate crew documents',
              onPress: generateCrewDocumentsModal.handleOpen,
            },
          ]}
          width={200}
        />
      </Popover>
      <GenerateProjectDocumentsForJobModal
        key={generateCrewDocumentsModal.key}
        isOpen={generateCrewDocumentsModal.isOpen}
        handleClose={generateCrewDocumentsModal.handleClose}
        project={project}
        onSuccess={() => {
          generateCrewDocumentsModal.handleClose();
          refetch();
        }}
      />
      <ProjectDocumentTemplateViewer
        key={'document-template-viewer'}
        documentTemplateViewer={overlayHandler}
        project={project}
        refetch={refetch}
      />
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
AddProjectDocumentsPopover.fragment = gql`
  ${ProjectDocumentTemplateViewer.fragment}
  ${GenerateProjectDocumentsForJobModal.fragment}
  fragment AddProjectDocumentsPopover on Project {
    id
    ...ProjectDocumentTemplateViewer
    ...GenerateProjectDocumentsForJobModal
  }
`;

export default AddProjectDocumentsPopover;
